import React, { useState } from "react";
import Bold from "../Bold";
import Italic from "../Italic";
import Underline from "../Underline";
import { AlignButton } from "../Alignments";
import { Dropdown } from "react-bootstrap";
import {
  $createParagraphNode,
  $getSelection,
  $isRangeSelection,
  ElementFormatType,
  LexicalEditor,
} from "lexical";
import { $setBlocksType } from "@lexical/selection";
import { $createHeadingNode, HeadingTagType } from "@lexical/rich-text";
import { ChevronDown, TickIcon } from "../../../../assets/icons/SvgIcons";
import { FormatOption } from "../../LexicalEditor.types";

const Format = ({
  editor,
  blockType,
  alignment,
  isBold,
  isItalic,
  isUnderline,
  readOnly,
}: {
  editor: LexicalEditor;
  alignment: ElementFormatType | undefined;
  blockType: any;
  active: boolean;
  isBold: boolean;
  isUnderline: boolean;
  isItalic: boolean;
  readOnly?: boolean;
}) => {
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const [selectedHeading, setSelectedHeading] = useState<HeadingTagType | null>(
    null
  );

  const formatParagraph = () => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        $setBlocksType(selection, () => $createParagraphNode());
      }
      setSelectedHeading(null);
    });
  };

  const formatHeading = (headingSize: HeadingTagType) => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        if (selectedHeading === headingSize) {
          $setBlocksType(selection, () => $createParagraphNode());
          setSelectedHeading(null);
        } else {
          $setBlocksType(selection, () => $createHeadingNode(headingSize));
          setSelectedHeading(headingSize);
        }
      }
    });
  };

  const formatOptions: FormatOption[] = [
    { component: Bold, props: { editor, isDropdown: true, active: isBold } },
    {
      component: Italic,
      props: { editor, isDropdown: true, active: isItalic },
    },
    {
      component: Underline,
      props: { editor, isDropdown: true, active: isUnderline },
    },
    {
      label: "Blocks",
      width: "185px",
      props: { editor, blockType: blockType },
      subItems: [
        <div className={`lexical-item`} onClick={formatParagraph}>
          <span className={`lexical-text lexical-paragraph`}>Paragraph</span>
          {blockType === "paragraph" && !selectedHeading && <TickIcon />}
        </div>,
        ...["h1", "h2", "h3", "h4", "h5", "h6"].map((tag, index) => (
          <div
            key={tag}
            className={`lexical-item`}
            onClick={() => formatHeading(tag as HeadingTagType)}
          >
            <span className={`lexical-text editor-heading-${tag}`}>
              Heading {index + 1}
            </span>
            {selectedHeading === tag && <TickIcon />}
          </div>
        )),
      ],
    },
    {
      label: "Align",
      props: { editor },
      width: "120px",
      subItems: ["left", "center", "right", "justify"].map((align) => (
        <AlignButton
          key={align}
          alignment={align as ElementFormatType}
          editor={editor}
          isFormat={true}
          currentAlignment={alignment}
        />
      )),
    },
  ];

  return (
    <Dropdown className="lexical-formatDropdown">
      <Dropdown.Toggle
        variant="primary"
        id="dropdown-basic"
        className={`lexical-toolbarItem ${readOnly ? "lexical-readOnly" : ""}`}
        as="div"
        disabled={readOnly}
      >
        Format
      </Dropdown.Toggle>
      <Dropdown.Menu className={`lexical-dropdown lexical-dropdown2`} as="div">
        {formatOptions.map((item, index) => {
          const Component = item.component;
          return (
            <React.Fragment key={index}>
              {item.subItems ? (
                <Dropdown
                  onMouseEnter={() => setHoverIndex(index)}
                  onMouseLeave={() => setHoverIndex(null)}
                  drop="right"
                  className="lexical-dropdownBgSecondary"
                  show={hoverIndex === index}
                >
                  <Dropdown.Toggle
                    as="button"
                    className={`lexical-toolbarItem2 lexical-toolbarItem lexical-chevronDownDropdown`}
                  >
                    <span style={{ marginLeft: "33px" }}>{item.label}</span>
                    <ChevronDown />
                  </Dropdown.Toggle>
                  <Dropdown.Menu
                    className={`lexical-dropdown lexical-dropdown2 lexical-dropdown3`}
                    style={{ width: item.width }}
                  >
                    {item.subItems.map((subItem, subIndex) => (
                      <Dropdown.Item
                        key={subIndex}
                        className={`lexical-toolbarItem lexical-dropdownSubItem`}
                        as="div"
                      >
                        {subItem}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
              ) : (
                Component && (
                  <Dropdown.Item
                    className={`lexical-dropdownBgSecondary ${
                      item.props.active ? "lexical-itemActive" : ""
                    }`}
                    as="div"
                  >
                    <Component {...item.props} />
                  </Dropdown.Item>
                )
              )}
            </React.Fragment>
          );
        })}
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default Format;
